import { useCallback } from 'react';

import { createEntityAdapter, createSlice } from '@reduxjs/toolkit';
import utcToZonedTime from 'date-fns-tz/utcToZonedTime';
import parseISO from 'date-fns/parseISO';

import { useAppDispatch, useAppSelector } from '@shared/hooks';
import type { Schedule } from '@shared/types/schedules';

const scheduleAdapter = createEntityAdapter<Schedule>();

const scheduleSlice = createSlice({
	name: 'schedules',
	initialState: scheduleAdapter.getInitialState(),
	reducers: {
		schedulesSet: scheduleAdapter.setAll,
	},
});

export const scheduleReducer = scheduleSlice.reducer;
export const scheduleActions = scheduleSlice.actions;

const scheduleSelectors = scheduleAdapter.getSelectors();

export const useSchedules = () => {
	const dispatch = useAppDispatch();
	const schedules = useAppSelector((state) =>
		scheduleSelectors.selectAll(state.schedule.schedules)
	);

	const setSchedules = (schedules: Schedule[]) => dispatch(scheduleActions.schedulesSet(schedules));

	const findSchedules = useCallback(
		(session: { start: Date; end: Date }, tz: string): Schedule | undefined => {
			if (schedules.length === 0) return;
			const { start, end } = session;
			return schedules.find((busy) => {
				return (
					utcToZonedTime(parseISO(busy.start_time), tz) <= start &&
					utcToZonedTime(parseISO(busy.end_time), tz) >= end
				);
			});
		},
		[schedules]
	);

	return {
		schedules,
		setSchedules,
		findSchedules,
	};
};

export const useSchedule = (id: number) => {
	const schedule = useAppSelector((state) =>
		scheduleSelectors.selectById(state.schedule.schedules, id)
	);

	return {
		schedule,
	};
};
